import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { api, setToken, getToken, removeToken } from "../services/api";
import { loginWithPasskey as loginWithPasskeyService } from "../services/webauthn.service";

export interface User {
  id: string;
  tenantId: string;
  nome: string;
  email: string;
  nivel: string;
  permissoes: string[];
}

export interface TenantBranding {
  nome: string;
  slug: string;
  corPrimaria: string;
  corSecundaria: string;
  logoUrl?: string;
  faviconUrl?: string;
}

interface AuthContextType {
  user: User | null;
  tenantBranding: TenantBranding | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /**
   * Login legado com senha. Mantido pra compatibilidade — a UI de login
   * ja nao expoe este fluxo, mas o backend ainda aceita como fallback
   * emergencial. Prefira loginWithPasskey.
   */
  login: (email: string, senha: string) => Promise<void>;
  /**
   * Login primario com WebAuthn/passkey (Face ID / Touch ID / Windows Hello / FIDO2).
   * Email e opcional — quando informado, o backend filtra credenciais permitidas.
   */
  loginWithPasskey: (email?: string) => Promise<void>;
  /**
   * Aplica manualmente uma sessao ja obtida por outro caminho
   * (ex: registro de passkey via convite, que retorna token+user).
   */
  applySession: (token: string, user: User, tenant?: TenantBranding) => void;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [tenantBranding, setTenantBranding] = useState<TenantBranding | null>(() => {
    const cached = localStorage.getItem("payflow_branding");
    return cached ? JSON.parse(cached) : null;
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = getToken();
    if (token) {
      api.get<User>("/auth/me")
        .then((response) => {
          if (response.data) {
            setUser(response.data);
            localStorage.setItem("payflow_user", JSON.stringify(response.data));
          }
        })
        .catch(() => {
          removeToken();
          localStorage.removeItem("payflow_user");
        })
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, []);

  const applySession = (token: string, userData: User, tenant?: TenantBranding) => {
    setToken(token);
    setUser(userData);
    localStorage.setItem("payflow_user", JSON.stringify(userData));
    if (tenant) {
      setTenantBranding(tenant);
      localStorage.setItem("payflow_branding", JSON.stringify(tenant));
    }
  };

  const login = async (email: string, senha: string) => {
    setIsLoading(true);
    try {
      const response = await api.post<{
        token: string;
        user: User;
        tenant?: TenantBranding;
      }>("/auth/login", {
        email,
        senha,
      });

      if (response.data) {
        applySession(response.data.token, response.data.user, response.data.tenant);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithPasskey = async (email?: string) => {
    setIsLoading(true);
    try {
      const session = await loginWithPasskeyService(email);
      applySession(session.token, session.user, session.tenant);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignora erro no logout - limpa sessao local de qualquer forma
    }
    removeToken();
    localStorage.removeItem("payflow_user");
    localStorage.removeItem("payflow_branding");
    setUser(null);
    setTenantBranding(null);
  };

  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    if (user.nivel === "master" || user.nivel === "admin") return true;
    return user.permissoes.includes(permission);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        tenantBranding,
        isLoading,
        isAuthenticated: !!user,
        login,
        loginWithPasskey,
        applySession,
        logout,
        hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
